import { NavLink } from "react-router-dom";

import { SummaryCard } from "../components/SummaryCard";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";
import { DetailCardGrid, DetailMetricGrid } from "./DeepDiveParts";

export function ConceptPage() {
  const { data, loading, error } = useContent("concept-page", api.getConcept);

  return (
    <PageContainer
      eyebrow="Supporting Detail - Concept & Program"
      title="Concept detail for the Concept & Program surface"
      lede="This route is the concept-specific supporting layer beneath Concept & Program. It keeps the ranking, fallback stack, and comparison detail available without acting like the primary investor-facing program surface."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading concept summary...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? (
        <div className="stack-xl">
          <DetailMetricGrid
            items={[
              { title: "Lead Concept", value: data.conceptSummary.leadConcept.concept, detail: data.conceptSummary.leadConcept.corePositioning },
              { title: "Fallback Concepts", value: `${data.conceptSummary.fallbackConcepts.length}`, detail: "Still alive in the concept stack" },
              { title: "Concept Options", value: `${data.conceptComparison.conceptOptions.length}`, detail: "Compared in the processed matrix" },
              { title: "Positioning Paths", value: `${data.conceptComparison.positioningOptions.length}`, detail: "Compared in the positioning matrix" }
            ]}
          />

          <DetailCardGrid
            items={[
              {
                eyebrow: "Lead Concept",
                title: data.conceptSummary.leadConcept.concept,
                description: data.conceptSummary.executiveRecommendation,
                fields: [
                  { label: "Core Positioning", value: data.conceptSummary.leadConcept.corePositioning },
                  { label: "Best-Fit Guest Thesis", value: data.conceptSummary.leadConcept.bestFitGuestThesis },
                  { label: "Why It Leads", value: data.conceptSummary.leadConcept.whyItLeads }
                ]
              },
              {
                eyebrow: "Current Best Read",
                title: "Why this concept currently leads",
                description: data.conceptComparison.currentBestRead,
                fields: [
                  { label: "Positioning Read", value: data.conceptComparison.positioningBestRead },
                  { label: "Current Role", value: data.conceptSummary.leadConcept.currentRole },
                  { label: "Comp Support", value: data.conceptSummary.leadConcept.currentCompSupport }
                ]
              }
            ]}
          />

          <div className="grid-two">
            <SummaryCard eyebrow="Why This Concept Leads" title="The current evidence favors control over forced density" description={data.conceptSummary.currentRecommendationRead}>
              <ul className="bullet-list">
                {data.conceptSummary.whyLead.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
            <SummaryCard eyebrow="Fallback Concepts" title="Alternatives stay alive, but they do not lead" description="The fallback stack remains useful for testing, not for replacing the base recommendation prematurely.">
              <ul className="bullet-list">
                {data.conceptSummary.fallbackConcepts.map((item) => (
                  <li key={item.concept} className="bullet-item">{`${item.concept}: ${item.whyItStaysAlive}`}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>

          <div className="grid-two">
            <SummaryCard eyebrow="Assumptions" title="What the recommendation is carrying" description="These assumptions matter because later zoning, cost, or underwriting work can still reorder the stack.">
              <ul className="bullet-list">
                {data.conceptSummary.assumptions.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
            <SummaryCard eyebrow="Still Needs Testing" title="What can still move the ranking" description="This page is a concept recommendation, not a final design decision.">
              <ul className="bullet-list">
                {data.conceptSummary.tests.map((item) => (
                  <li key={item} className="bullet-item">{item}</li>
                ))}
              </ul>
            </SummaryCard>
          </div>

          <DetailCardGrid
            items={data.conceptComparison.conceptOptions.slice(0, 4).map((item) => ({
              eyebrow: "Concept Matrix",
              title: item.concept,
              description: item.description,
              fields: [
                { label: "Why It Fits", value: item.whyItFits },
                { label: "Main Risk", value: item.mainRisk },
                { label: "Rank", value: item.currentRank }
              ]
            }))}
          />

          <SummaryCard eyebrow="Conclusion" title={data.conceptSummary.status} description={data.conceptSummary.conclusion} />

          <SummaryCard
            eyebrow="Parent Surface"
            title="Return to Concept & Program for the full selection frame"
            description="Use this page for concept-comparison depth. Use Concept & Program for the full investor-facing explanation of why the lead concept wins and what still remains conditional."
          >
            <div className="row-wrap gap-sm overview-go-deeper-actions">
              <NavLink to="/concept-program" className="link-button link-button-secondary">
                Review Concept & Program
              </NavLink>
              <NavLink to="/market-support" className="link-button link-button-secondary">
                Review Market Support
              </NavLink>
            </div>
          </SummaryCard>
        </div>
      ) : null}
    </PageContainer>
  );
}
